import React, { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Store, CloudUpload, X, Image as ImageIcon, Info } from "lucide-react";
import SectionHeader from "../../components/ui/SectionHeader";

import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import {
  updateVendorPage,
  UpdateVendorPagePayload,
  updateVendorPageImages,
  deleteVendorPageImage,
} from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

const VendorPageSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } =
    useOutletContext<SettingsOutletContextType>();

  const [businessDescription, setBusinessDescription] = useState("");
  const [seoDescription, setSeoDescription] = useState("");

  const [uploadedOGImage, setUploadedOGImage] = useState<string | null>(null);
  const [uploadedOGImageName, setUploadedOGImageName] = useState<string>("");
  const [ogImageFile, setOgImageFile] = useState<File | null>(null);

  const handleOGImageUpload = (image: string, fileName: string, file?: File) => {
    setUploadedOGImage(image);
    setUploadedOGImageName(fileName);
    if (file) {
      setOgImageFile(file);
    }
  };

  const handleOGImageRemove = () => {
    setUploadedOGImage(null);
    setUploadedOGImageName("");
    setOgImageFile(null);
  };

  interface GalleryImage {
    url: string;
    name: string;
    size: number;
    file?: File; // For new uploads
    isExisting?: boolean; // True if it's an existing image from server
    filename?: string; // Server filename for existing images
  }

  const [uploadedGalleryImages, setUploadedGalleryImages] = useState<
    GalleryImage[]
  >([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState<number>(0);
  const [isGalleryDragging, setIsGalleryDragging] = useState(false);
  const galleryFileInputRef = React.useRef<HTMLInputElement>(null);

  const [seoKeywords, setSeoKeywords] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Helper function to get full image URL
  const getImageUrl = (img: string | null | undefined, isVendorPageImage: boolean = false): string | null => {
    if (!img) return null;
    const imgStr = String(img);
    if (!imgStr || imgStr.trim() === "") return null;

    // If already a full URL or data URL, return as is
    if (imgStr.startsWith("http://") || imgStr.startsWith("https://") || imgStr.startsWith("data:") || imgStr.startsWith("blob:")) {
      return imgStr;
    }

    // If it's a relative path starting with /, prepend base URL
    if (imgStr.startsWith("/")) {
      return `https://salonify.co.uk${imgStr}`;
    }

    // For vendor page images, construct path: user-uploads/vendor-page/{company_id}/filename
    if (isVendorPageImage && settingsData?.vendor_page?.company_id) {
      return `https://salonify.co.uk/user-uploads/vendor-page/${settingsData.vendor_page.company_id}/${imgStr}`;
    }

    // Otherwise, assume it's a storage path and prepend base URL
    return `https://salonify.co.uk/storage/${imgStr}`;
  };

  React.useEffect(() => {
    if (settingsData && settingsData.vendor_page) {
      const vp = settingsData.vendor_page;
      setBusinessDescription(vp.description || "");
      setSeoDescription(vp.seo_description || "");
      setUploadedOGImage(getImageUrl(vp.og_image));
      if (vp.seo_keywords) {
        setSeoKeywords(
          vp.seo_keywords
            .split(",")
            .map((k) => k.trim())
            .filter((k) => k)
        );
      }

      // Handle Gallery Images
      // Parse images from JSON string: [{"name":"filename.jpg","size":12345,"type":"image/jpeg"}]
      const images: GalleryImage[] = [];

      if (vp.images) {
        try {
          const imagesArray = typeof vp.images === 'string' ? JSON.parse(vp.images) : vp.images;

          if (Array.isArray(imagesArray)) {
            imagesArray.forEach((img: any) => {
              const filename = img.name || img;
              if (filename) {
                // Construct full URL from filename - vendor page images need special path
                const fullUrl = getImageUrl(filename, true);
                console.log("Vendor page image:", { filename, fullUrl, company_id: vp.company_id });
                if (fullUrl) {
                  const isDefault = vp.default_image === filename;
                  const imageIndex = images.length;
                  images.push({
                    url: fullUrl,
                    name: isDefault ? "Default Image" : "Existing Image",
                    size: img.size || 0,
                    isExisting: true,
                    filename: filename,
                  });

                  // Set default image index
                  if (isDefault) {
                    setDefaultImageIndex(imageIndex);
                  }
                }
              }
            });
          }
        } catch (error) {
          console.error("Failed to parse images JSON:", error);
        }
      }

      // Fallback: if no images from JSON, check photos_without_default_image
      if (images.length === 0 && settingsData.vendor_page.photos_without_default_image) {
        settingsData.vendor_page.photos_without_default_image.forEach((img: any) => {
          const imgUrl = typeof img === "string" ? img : img.image_url || img.url || "";
          if (imgUrl) {
            const filename = imgUrl.split('/').pop() || imgUrl;
            const fullUrl = getImageUrl(imgUrl);
            if (fullUrl) {
              images.push({
                url: fullUrl,
                name: "Existing Image",
                size: 0,
                isExisting: true,
                filename: filename,
              });
            }
          }
        });
      }

      setUploadedGalleryImages(images);
    }
  }, [settingsData]);

  const handleSave = async () => {
    if (!settingsData?.vendor_page) return;
    setSaving(true);
    try {
      console.log("VendorPageSettings handleSave - ogImageFile:", {
        isFile: ogImageFile instanceof File,
        name: ogImageFile?.name,
        size: ogImageFile?.size,
        type: ogImageFile?.type,
        value: ogImageFile
      });

      const payload: UpdateVendorPagePayload = {
        description: businessDescription,
        seo_description: seoDescription,
        seo_keywords: seoKeywords.join(","),
        map_option: settingsData.vendor_page.map_option || "deactive",
        latitude: settingsData.vendor_page.latitude || 0,
        longitude: settingsData.vendor_page.longitude || 0,
        og_image: ogImageFile || undefined,
        default_image: settingsData.vendor_page.default_image || undefined,
      };
      const response = await updateVendorPage(settingsData.vendor_page.id, payload);

      // Update OG image from response if available
      if (response.success && response.data?.vendor_page?.og_image) {
        const imageUrl = getImageUrl(response.data.vendor_page.og_image);
        setUploadedOGImage(imageUrl);
        setUploadedOGImageName(""); // Clear file name since we now have server URL
        setOgImageFile(null); // Clear file reference after successful upload
      }

      // Handle gallery images update
      if (uploadedGalleryImages.length > 0) {
        const newFiles = uploadedGalleryImages
          .filter(img => img.file && !img.isExisting)
          .map(img => img.file!);

        const existingFilenames = uploadedGalleryImages
          .filter(img => img.isExisting && img.filename)
          .map(img => img.filename!);

        const defaultImageFilename = uploadedGalleryImages[defaultImageIndex]?.filename ||
          (uploadedGalleryImages[defaultImageIndex]?.file?.name || '');

        if (newFiles.length > 0 || existingFilenames.length > 0) {
          await updateVendorPageImages({
            vendor_page_id: settingsData.vendor_page.id,
            file: newFiles.length > 0 ? newFiles : undefined,
            uploaded_files: existingFilenames.length > 0 ? JSON.stringify(existingFilenames.map(f => ({ name: f }))) : undefined,
            default_image: defaultImageFilename,
          });
        }
      }

      toast.success("Vendor page settings updated successfully!");
      refreshSettings();
    } catch (error: any) {
      console.error("Failed to update vendor page settings", error);
      toast.error("Failed to update vendor page settings. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKeyword = (indexToRemove: number) => {
    setSeoKeywords((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const [newKeyword, setNewKeyword] = useState("");

  const handleAddKeyword = () => {
    const trimmed = newKeyword.trim();
    if (trimmed && !seoKeywords.includes(trimmed)) {
      setSeoKeywords((prev) => [...prev, trimmed]);
      setNewKeyword("");
    }
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };
  const handleGalleryDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsGalleryDragging(true);
  };

  const handleGalleryDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsGalleryDragging(false);
  };

  const handleGalleryDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsGalleryDragging(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => {
      if (
        file &&
        file.type.startsWith("image/") &&
        file.size <= 2 * 1024 * 1024
      ) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedGalleryImages((prev) => [
            ...prev,
            {
              url: e.target?.result as string,
              name: file.name,
              size: file.size,
              file: file, // Store the File object for upload
              isExisting: false,
            },
          ]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleGalleryUploadClick = () => {
    galleryFileInputRef.current?.click();
  };

  const handleGalleryFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      if (
        file &&
        file.type.startsWith("image/") &&
        file.size <= 2 * 1024 * 1024
      ) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedGalleryImages((prev) => [
            ...prev,
            {
              url: e.target?.result as string,
              name: file.name,
              size: file.size,
              file: file, // Store the File object for upload
              isExisting: false,
            },
          ]);
        };
        reader.readAsDataURL(file);
      }
    });
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveGalleryImage = async (indexToRemove: number) => {
    const imageToRemove = uploadedGalleryImages[indexToRemove];

    // If it's an existing image, delete it from server
    if (imageToRemove.isExisting && imageToRemove.filename && settingsData?.vendor_page) {
      try {
        const defaultImage = uploadedGalleryImages[defaultImageIndex]?.filename || '';
        await deleteVendorPageImage({
          vendorId: settingsData.vendor_page.id,
          file: imageToRemove.filename,
          default_image: defaultImage,
        });
        toast.success("Image removed successfully!");
        refreshSettings();
      } catch (error: any) {
        console.error("Failed to delete image:", error);
        toast.error("Failed to delete image. Please try again.");
        return; // Don't remove from UI if deletion failed
      }
    }

    // Remove from UI
    setUploadedGalleryImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
    if (defaultImageIndex === indexToRemove) {
      setDefaultImageIndex(0);
    } else if (defaultImageIndex > indexToRemove) {
      setDefaultImageIndex(defaultImageIndex - 1);
    }
  };

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading vendor settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Vendor Page Settings"
        description="Customize your vendor page appearance and SEO settings"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Store size={18} className="text-white" />
            Page Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1">
          <div className="space-y-5">
            {/* OG Image & Business Description Section */}
            <div>
              <SectionHeader title="Page Content" />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - OG Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    OG Image <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {/* Image Preview or Upload Area */}
                    <div className="mb-3 relative">
                      {uploadedOGImage ? (
                        <>
                          <div className="bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center h-40">
                            <img
                              src={uploadedOGImage}
                              alt="OG Preview"
                              className="max-h-full max-w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={handleOGImageRemove}
                            className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 z-10"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <div
                          onClick={() =>
                            document.getElementById("og-image-input")?.click()
                          }
                          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#885ABB] hover:bg-gray-50 transition-all h-40 flex flex-col items-center justify-center"
                        >
                          <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600 font-medium">
                            Click to upload
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            or drag and drop
                          </p>
                        </div>
                      )}
                      <input
                        id="og-image-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              handleOGImageUpload(
                                e.target?.result as string,
                                file.name,
                                file
                              );
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                      />
                    </div>

                    {/* Image Info */}
                    <div className="space-y-1">
                      {uploadedOGImageName && (
                        <p className="text-xs text-gray-600 truncate">
                          📎 {uploadedOGImageName}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Info size={11} />
                        Recommended: 1200x630px for social sharing
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Business Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Description
                  </label>
                  <textarea
                    value={businessDescription}
                    onChange={(e) => setBusinessDescription(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 rounded-lg bg-gray-100 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all duration-150 ease-in-out outline-none border border-transparent focus:border-gray-300 focus:ring-1 focus:ring-purple-500/40 hover:bg-gray-50 focus:bg-white resize-none"
                    placeholder="Enter a detailed description of your business..."
                  />
                  <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                    <Info size={12} />
                    Describe your business, services, and what makes you unique
                  </p>
                </div>
              </div>
            </div>

            {/* Business Gallery Section */}
            <div>
              <SectionHeader title="Business Gallery" />

              {/* Info Box */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Info size={14} className="flex-shrink-0" />
                  Image size should be less than 2 MB. Drag and drop or click to
                  upload multiple images.
                </p>
              </div>

              {/* Upload Area */}
              <div
                onDragOver={handleGalleryDragOver}
                onDragLeave={handleGalleryDragLeave}
                onDrop={handleGalleryDrop}
                onClick={handleGalleryUploadClick}
                className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center min-h-[180px] cursor-pointer transition-all duration-200 mb-4 ${isGalleryDragging
                    ? "border-[#885ABB] bg-purple-50"
                    : "border-gray-300 bg-gray-100 hover:border-gray-400"
                  }`}
              >
                <CloudUpload
                  className="w-12 h-12 text-[#885ABB] mb-3"
                  strokeWidth={1.5}
                />
                <p className="text-sm text-gray-700 font-medium mb-1">
                  Drop files here to upload
                </p>
                <p className="text-xs text-gray-500">or click to browse</p>
                <input
                  ref={galleryFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryFileInputChange}
                  className="hidden"
                />
              </div>

              {/* Uploaded Images Grid */}
              {uploadedGalleryImages.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {uploadedGalleryImages.map((image, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      {/* Image Thumbnail */}
                      <div className="mb-3 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center h-32 relative group">
                        <img
                          src={image.url}
                          alt={image.name}
                          className="max-h-full max-w-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <ImageIcon className="text-white" size={32} />
                        </div>
                      </div>

                      {/* File Info */}
                      <div className="text-center mb-3">
                        <p className="text-sm font-semibold text-gray-700">
                          {(image.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {image.name}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="space-y-2">
                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemoveGalleryImage(index)}
                          className="w-full text-sm text-red-600 hover:text-red-700 font-medium transition-colors py-1"
                        >
                          Remove File
                        </button>

                        {/* Make Default Image Checkbox */}
                        <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-200">
                          <input
                            type="checkbox"
                            id={`default-${index}`}
                            checked={defaultImageIndex === index}
                            onChange={() => setDefaultImageIndex(index)}
                            className="w-4 h-4 accent-[#885ABB] cursor-pointer"
                          />
                          <label
                            htmlFor={`default-${index}`}
                            className="text-sm text-gray-700 cursor-pointer"
                          >
                            Default image
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {uploadedGalleryImages.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No images uploaded yet
                </div>
              )}
            </div>

            {/* SEO Settings Section */}
            <div>
              <SectionHeader title="SEO Settings" />
              <div className="space-y-4">
                {/* Business SEO Keywords */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SEO Keywords
                  </label>
                  <div className="p-3 bg-gray-100 rounded-lg border border-gray-200">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {seoKeywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="bg-[#885ABB] text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-2 hover:bg-[#7748A4] transition-colors"
                        >
                          {keyword}
                          <button
                            onClick={() => handleRemoveKeyword(index)}
                            className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                            title="Remove keyword"
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={handleKeywordKeyPress}
                        placeholder="Type keyword and press Enter"
                        className="flex-1 px-3 py-2 rounded-lg bg-white text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all duration-150 ease-in-out outline-none border border-gray-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40"
                      />
                      <button
                        onClick={handleAddKeyword}
                        className="px-4 py-2 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                    <Info size={12} />
                    Type keywords and press Enter or click Add. Click the X to remove keywords.
                  </p>
                </div>

                {/* Business SEO Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SEO Description
                  </label>
                  <textarea
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    rows={5}
                    className="w-full px-3 py-2 rounded-lg bg-gray-100 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all duration-150 ease-in-out outline-none border border-transparent focus:border-gray-300 focus:ring-1 focus:ring-purple-500/40 hover:bg-gray-50 focus:bg-white resize-none"
                    placeholder="Enter SEO description..."
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorPageSettings;
